import React, { useContext, useEffect, useRef, useState } from "react";
import { ThemeConsumer } from "styled-components";

import { CopyrightOverlay } from "@/common/CopyrightOverlay";
import { Footer } from "@/common/Footer";
import { Intro } from "@/common/Intro";
import { renderOutro } from "@/common/Outro";
import Slider, { SlideProps, SliderActions } from "@/common/Slider";
import Header from "@/components/Header";
import { SharingType } from "@/components/Sharing";
import { Subline } from "@/components/Subline";
import { Group, Main, Stage, Wrapper } from "@/containers/styles";
import {
  GlobalContext,
  VotingConsumer,
  VotingContextProps,
  VotingProvider,
} from "@/contexts";
import LocaleContext from "@/i18n";
import EventSystem from "@/service/EventSystem";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  InteractionIntro,
  Option,
  OutroType,
  RegistrationStatus,
  Slide,
  Voting,
  VotingResult,
  VotingResultsContainer,
  VotingSlide,
  VotingVote,
} from "@/service/Model";
import { isTouchDevice } from "@/utils/getters";
import { getSharingTitle, handleShare } from "@/utils/socialActions";

import Svg from "../../styles/utils/Svg";
import VotingOptions from "./components/VotingOptions";
// Localized Components
import {
  InvertedButton,
  LeftArrowWrapper,
  RightArrowWrapper,
  Summary,
  TextBox,
  VoteButton,
} from "./styles";

type VotingContainerProps = {
  interaction: Voting;
  interactionResults: VotingResultsContainer;
};

type Overlay = {
  closable: boolean;
  message: string;
  autoFadeOut: boolean;
};

const filterVotesBySlideId = (votes: VotingVote[], slideId: string) =>
  votes.filter((vote: VotingVote) => vote.slideId === slideId);

const filterOptionsByVotes = (options: Option[], votes: VotingVote[]) =>
  options.filter((option: Option) =>
    votes.find((vote: VotingVote) => vote.optionId === option.id)
  );

const sortOptionsByList = (options: Option[], sortList: string[]) => {
  options.sort(
    (a: Option, b: Option) => sortList.indexOf(a.id) - sortList.indexOf(b.id)
  );
};

const VotingContainer: React.FC<VotingContainerProps> = (
  props: VotingContainerProps
): React.JSX.Element => {
  const globalContext = useContext(GlobalContext);
  const localeContext = useContext(LocaleContext);
  const { interaction, interactionResults } = props;

  const defaultBackground = interaction.background;
  const defaultBackgroundColor = interaction.backgroundColor;

  const laHyperScrollFinishedRef = useRef(false);

  const [showOutro, setShowOutro] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [showTitle, setShowTitle] = useState<boolean>(false);
  const [navigation, setNavigation] = useState<{
    previous: boolean;
    next: boolean;
  }>({
    previous: true,
    next: true,
  });
  const [footNote, setFootNote] = useState<string>("");
  const [overlay, setOverlay] = useState<Overlay | undefined>(undefined);
  const [showImageCopyrights, setShowImageCopyrights] =
    useState<boolean>(false);

  const imagesData = InteractionService.getImagesData(interaction);
  const expired = !!(interaction.endDate && interaction.endDate < Date.now());

  useEffect(() => {
    // Register on container when scroll transition ended
    IFrameService.hasScrolledListener();

    // This event is triggered by la-hyper
    EventSystem.subscribe("parent:scroll:finished", () => {
      laHyperScrollFinishedRef.current = true;
      setTimeout(() => handleCustomSlideChange(SliderActions.next), 300);
    });

    if (expired) {
      setOverlay({
        autoFadeOut: false,
        closable: true,
        message: localeContext.translation.endedPoll,
      });
    }
  }, []);

  const handleShareClick = (type: SharingType): void => {
    const sharingTitle = getSharingTitle(interaction);
    handleShare(type, sharingTitle, globalContext?.brandSettings?.xHandle);
  };

  const onSubmitEnd = (
    status: RegistrationStatus,
    votingContext: VotingContextProps
  ): void => {
    if (status !== RegistrationStatus.Success) {
      return;
    } else if (votingContext.participated) {
      votingContext.register();
      return;
    }
    // Hint: Special case: The voting results will be send
    // after the registration outro has been submitted.
    votingContext.submit();
  };

  const handleCustomSlideChange = (
    direction: SliderActions,
    slideIndex?: number
  ) => {
    IFrameService.reportAnswerAction();
    EventSystem.publish(`slider:action:${direction}`, slideIndex);
  };

  const handleSlideChange = (
    nextSlideId: string,
    nextSlideTitle: string,
    hasPrevious: boolean,
    hasNext: boolean
  ): void => {
    const { translation } = localeContext;
    const totalQuestionSlides = interaction.slides.length;
    const questionSlideIndex: number = interaction.slides.findIndex(
      (slide: Slide) => slide.id === nextSlideId
    );

    setTitle(nextSlideTitle);
    setShowTitle(true);
    setNavigation({ previous: hasPrevious, next: hasNext });

    if (nextSlideId === "notice" || nextSlideId === "intro") {
      setFootNote("");
    } else if (
      nextSlideId === "summary" ||
      nextSlideId === "top-votes-summary"
    ) {
      setFootNote(translation.result);
    } else {
      setFootNote(`${questionSlideIndex + 1}/${totalQuestionSlides}`);
    }
  };

  const handleNextSlide = (delay: number): void => {
    setTimeout(() => {
      if (globalContext.hyperAvailable) {
        laHyperScrollFinishedRef.current = false;
        IFrameService.scroll();
        // la-hyper scroll:finished event received? (see useEffect)
        // If not scroll manually after timeout
        setTimeout(() => {
          if (!laHyperScrollFinishedRef.current) {
            laHyperScrollFinishedRef.current = true;
            handleCustomSlideChange(SliderActions.next);
          }
        }, 2000);
      } else {
        handleCustomSlideChange(SliderActions.next);
      }
    }, delay);
  };

  const renderSliderOverlays = (): React.JSX.Element => {
    return (
      <React.Fragment>
        {overlay && (
          <Subline
            overlay={true}
            dismiss={overlay.autoFadeOut}
            showCloseButton={overlay.closable}
            onClose={() => setOverlay(undefined)}
          >
            {overlay.message}
          </Subline>
        )}
        {showImageCopyrights && (
          <CopyrightOverlay
            onClose={() => setShowImageCopyrights(false)}
            imagesData={imagesData}
          />
        )}
      </React.Fragment>
    );
  };

  const getVotedSlideIds = (votes: VotingVote[]): string[] =>
    interaction.slides
      .filter((slide: Slide) =>
        votes.find((vote: VotingVote) => vote.slideId === slide.id)
      )
      .map((slide: Slide) => slide.id);

  const quizCompleted = (votedSlideIds: string[]): boolean =>
    votedSlideIds.length === interaction.slides.length;

  const getSliderPosition = (votedSlideIds: string[]): number => {
    const slideCount = votedSlideIds.length;
    if (!expired && slideCount) {
      return interaction.intro ? slideCount + 1 : slideCount;
    }
    return 0;
  };

  const renderSlider = (
    votingContext: VotingContextProps
  ): React.JSX.Element => {
    const { votes } = votingContext;
    const votedSlideIds = getVotedSlideIds(votes);
    const stoppedAtSlide = getSliderPosition(votedSlideIds);
    const completed = quizCompleted(votedSlideIds);

    return (
      <ThemeConsumer>
        {(theme: ITheme) => {
          return (
            <Slider
              timing={400}
              selected={stoppedAtSlide}
              slides={slides}
              backgroundImageUrl={defaultBackground}
              backgroundColor={
                defaultBackgroundColor ||
                theme.colors.votingBackground ||
                theme.colors.sliderBackground
              }
              isSlideVoted={(slideId: string) =>
                votedSlideIds.includes(slideId)
              }
              onSlideWillChange={(index: number) => {
                // fadeOut animations
                if (!slides[index]) {
                  return;
                }
                setShowTitle(false);
                setShowOutro(false);
                if (overlay) {
                  setOverlay({ ...overlay, autoFadeOut: true });
                }
              }}
              onSlideChanged={(index: number) => {
                // fadeIn animations
                if (!slides[index]) {
                  return;
                }
                const slide = slides[index];
                let hasPrevious = false,
                  hasNext = false;

                if (completed) {
                  hasPrevious = index > 0;
                  hasNext = index < slides.length - 1;
                  if (slide.key === "summary" && hasNext) {
                    hasNext = expired || votingContext.participated;
                  }
                }

                handleSlideChange(
                  slide.key,
                  slide.meta?.title || "",
                  hasPrevious,
                  hasNext
                );
              }}
            />
          );
        }}
      </ThemeConsumer>
    );
  };

  const renderVoting = (
    votingContext: VotingContextProps
  ): React.JSX.Element => {
    const { participantsCount } = interactionResults;
    const { participated } = votingContext;

    const participantsCountPlusVote = participated
      ? participantsCount + 1
      : participantsCount;

    return (
      <Wrapper>
        <Header
          icons={globalContext.icons}
          visible={showTitle}
          navigation={navigation}
          onNavigationClick={(
            e: React.MouseEvent,
            direction: SliderActions
          ) => {
            handleCustomSlideChange(direction);
          }}
        >
          {title}
        </Header>
        <Stage>
          <Group>
            <Main>
              {renderSlider(votingContext)}
              {renderSliderOverlays()}
            </Main>
            <Footer
              participants={participantsCountPlusVote}
              hideParticipantsCount={interaction.hideParticipantsCount}
              leftText={footNote}
              onShareClick={handleShareClick}
              onCopyrightClick={() => setShowImageCopyrights(true)}
              hasCopyrights={imagesData.length > 0}
            />
          </Group>
        </Stage>
      </Wrapper>
    );
  };

  const renderOutroOverlay = (
    votingContext: VotingContextProps
  ): React.JSX.Element | null => {
    const outro = props.interaction.outro;

    if (!outro || !showOutro) {
      return null;
    }

    return renderOutro(
      outro,
      { globals: globalContext },
      () => setShowOutro(!showOutro),
      (status: RegistrationStatus) => onSubmitEnd(status, votingContext)
    );
  };

  const generateIntroSlide = (intro: InteractionIntro): SlideProps => {
    const backgroundImageUrl = intro.background || defaultBackground;
    return {
      key: "intro",
      meta: { title: intro.title },
      backgroundImageUrl,
      backgroundColor: defaultBackgroundColor,
      children: (
        <Intro
          text={intro.text}
          label={intro.label}
          onButtonClick={() => handleNextSlide(0)}
          hasBackgroundImage={!!backgroundImageUrl}
          hideButton={expired}
        />
      ),
    };
  };

  const generateReadersChoiceSummarySlide = (
    userVotes: VotingVote[]
  ): SlideProps => {
    return {
      key: "top-votes-summary",
      meta: { title: interaction.title },
      backgroundImageUrl: defaultBackground,
      backgroundColor: defaultBackgroundColor,
      children: (
        <Summary>
          <Subline>
            {"Vielen Dank für Ihre Teilnahme!"}
            <br />
            {`So haben die anderen ${interaction.organizationId}-Leser abgestimmt:`}
          </Subline>
          {interaction.slides.map((slide: VotingSlide, index: number) => {
            // Hint: Don't show empty summary slide.
            // Only the first participant will have empty results - use userVotes instead.
            const votes = interactionResults.results.length
              ? interactionResults.results
              : userVotes;

            const slideTopVotes = filterVotesBySlideId(votes, slide.id)
              .sort((a: VotingResult, b: VotingResult) => b.value - a.value)
              .slice(0, slide.voteCount);

            const slideTopOptions = filterOptionsByVotes(
              slide.options,
              slideTopVotes
            );

            sortOptionsByList(
              slideTopOptions,
              slideTopVotes.map((vote: VotingVote) => vote.optionId)
            );

            const slideSummary: VotingSlide = {
              ...slide,
              options: slideTopOptions,
            };

            const slideUserVotes = filterVotesBySlideId(userVotes, slide.id);

            return (
              <VotingOptions
                key={`top-votes-summary-slide-${index}`}
                interactionId={interaction.id}
                slide={slideSummary}
                name={slide.title}
                votes={slideUserVotes}
                timing={150}
                isSummary={true}
                showCheckmarks={true}
                voteCount={0}
              />
            );
          })}
          <VoteButton
            onClick={() => handleCustomSlideChange(SliderActions.prev)}
          >
            <LeftArrowWrapper>
              <Svg
                contents={globalContext.icons.PageLeft}
                height={26}
                width={26}
              />
            </LeftArrowWrapper>
            {localeContext.translation.showYourSelection}
          </VoteButton>
        </Summary>
      ),
    };
  };

  const generateSummarySlide = (
    votingContext: VotingContextProps
  ): SlideProps => {
    const { outro } = interaction;
    const { participated, registered } = votingContext;

    const registrationSpecificComponents = (): React.JSX.Element => {
      if (outro?.outroType !== OutroType.RegistrationForm) {
        return <></>;
      }

      // Hint: Special case: The voting results will be send later
      // after the registration outro has been submitted. See onSubmitEnd().
      if (outro.onlyWithRegistration && !participated) {
        return (
          <TextBox
            dangerouslySetInnerHTML={{
              __html: `<span>${localeContext.translation.onlyWithRegistration}</span>`,
            }}
          />
        );
      }
      // Ability to reopen the outro after closing the outro
      if (participated && !registered) {
        return (
          <InvertedButton onClick={() => setShowOutro(true)}>
            {localeContext.translation.registerNowCall}
          </InvertedButton>
        );
      }

      return <></>;
    };

    const submitVoting = (): void => {
      if (
        outro?.outroType === OutroType.RegistrationForm &&
        outro.onlyWithRegistration
      ) {
        // Hint: Special case: The voting results will be send later
        // after the registration outro has been submitted. See onSubmitEnd().
        setShowOutro(true);
        return;
      }

      votingContext.submit();

      if (interaction.secret) {
        setOverlay({
          autoFadeOut: false,
          closable: false,
          message: localeContext.translation.thankYou,
        });
        if (globalContext.hyperAvailable) {
          IFrameService.scroll();
        }
      }
    };

    return {
      key: "summary",
      meta: { title: interaction.title },
      backgroundImageUrl: defaultBackground,
      backgroundColor: defaultBackgroundColor,
      children: (
        <Summary>
          {interaction.slides.map((slide: VotingSlide, index: number) => {
            const slideVotes = filterVotesBySlideId(
              votingContext.votes,
              slide.id
            );
            const votedSlideOptions = filterOptionsByVotes(
              slide.options,
              slideVotes
            );

            const slideSummary: VotingSlide = {
              ...slide,
              options: votedSlideOptions,
            };

            return (
              <VotingOptions
                key={`summary-votes-${index}`}
                interactionId={interaction.id}
                slide={slideSummary}
                name={
                  slide.voteLabel || localeContext.translation.yourSelection
                }
                votes={slideVotes}
                timing={150}
                isEnded={expired || votingContext.participated}
                isSummary={true}
                voteCount={0}
                showResults={
                  !interaction.secret && (expired || votingContext.participated)
                }
              />
            );
          })}
          {registrationSpecificComponents()}
          <VoteButton
            disabled={expired || votingContext.participated}
            onClick={() => submitVoting()}
          >
            {localeContext.translation.submitSelection}
          </VoteButton>
          {!interaction.secret && (
            <InvertedButton
              disabled={expired || !votingContext.participated}
              onClick={() => {
                handleNextSlide(0);
                if (globalContext.hyperAvailable) {
                  IFrameService.scroll();
                }
                // Hint: 8s to read the next summary.
                setTimeout(() => {
                  setShowOutro(true);
                }, 8000);
              }}
            >
              {localeContext.translation.showReadersChoice}
              <RightArrowWrapper>
                <Svg
                  contents={globalContext.icons.PageRight}
                  height={26}
                  width={26}
                />
              </RightArrowWrapper>
            </InvertedButton>
          )}
        </Summary>
      ),
    };
  };

  const generateVotingSlides = (
    votingContext: VotingContextProps
  ): SlideProps[] => {
    const touchDevice = isTouchDevice();

    return interaction.slides.map((slide: VotingSlide, index: number) => {
      const { options } = slide;
      const optionFadeInDelay = touchDevice && options.length > 15 ? 0 : 150;
      const nextSlideDelay = 1500;

      return {
        key: slide.id,
        meta: { title: interaction.title },
        backgroundImageUrl: slide.background || defaultBackground,
        backgroundColor: defaultBackgroundColor,
        children: (
          <VotingOptions
            key={index}
            interactionId={interaction.id}
            slide={slide}
            name={slide.title}
            description={slide.description}
            voteCount={slide.voteCount}
            votes={votingContext.votes.filter(
              (vote: VotingVote) => vote.slideId === slide.id
            )}
            timing={optionFadeInDelay}
            isEnded={expired || votingContext.participated}
            onVotingChange={(votes: VotingVote[]) => {
              setNavigation({
                previous: navigation.previous,
                next: votingContext.finished()
                  ? slide.voteCount === votes.length
                  : votingContext.slideVoted(slide.id),
              });
            }}
            onVotingEnd={(votes: VotingVote[]) => {
              votingContext.addVotes(slide, votes);
              if (votingContext.finished()) {
                return;
              }

              EventSystem.publish("slider:disabled", true);
              setTimeout(() => {
                handleNextSlide(0);
                EventSystem.publish("slider:disabled", false);
              }, nextSlideDelay);
            }}
          />
        ),
      };
    });
  };

  const generateSlides = (votingContext: VotingContextProps): SlideProps[] => {
    let slideProps: SlideProps[] = [];
    // Add intro
    if (interaction.intro) {
      slideProps.push(generateIntroSlide(interaction.intro));
    }

    // Add voting slides
    slideProps = [...slideProps, ...generateVotingSlides(votingContext)];

    // Add summary slide
    slideProps.push(generateSummarySlide(votingContext));

    if (!interaction.secret) {
      // Add readers choice summary slide
      slideProps.push(generateReadersChoiceSummarySlide(votingContext.votes));
    }

    return slideProps;
  };

  let slides: SlideProps[] = [];

  return (
    <VotingProvider
      interaction={interaction}
      interactionResults={interactionResults}
      preview={!!globalContext.query.preview}
    >
      <VotingConsumer>
        {(votingContext: VotingContextProps) => {
          slides = generateSlides(votingContext);

          return (
            <React.Fragment>
              {renderVoting(votingContext)}
              {renderOutroOverlay(votingContext)}
            </React.Fragment>
          );
        }}
      </VotingConsumer>
    </VotingProvider>
  );
};

export default VotingContainer;
export { VotingContainer };
